<script setup lang="ts">
import CreatePolicyDialog from '@/components/asicw/policies/create-dialog/CreatePolicyDialog.vue'
import { usePolicies } from '@/composables/asicw/policies'
import { dateFormatter } from '@/helpers/ep/formatters'
import { PolicyGroupStatus, type IPolicyGroup } from '@/types/asicw/policies'
import type { IPolicyGroupFilters } from '@/types/asicw/policy-groups'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import { QuestionFilled, Search } from '@element-plus/icons-vue'
import { Icon } from '@iconify/vue'
import { useDebounceFn } from '@vueuse/core'
import { reactive, ref } from 'vue'
import RcCustomerSelect from '../common/RcCustomerSelect.vue'
import { useOIDCStore } from '@/stores/oidc.store'
import StatusBadge from '@/components/common/StatusBadge.vue'
import UpdatePolicyGroupDialog from '@/components/asicw/policies/UpdatePolicyGroupDialog.vue'

const { isCustomer, isASICWOperationalUser } = useOIDCStore()

const props = defineProps<{
  customerId?: number
}>()

// Filters
const filters = reactive<IPolicyGroupFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[0],
  customer_id: props.customerId,
  number: undefined,
  status: undefined,
})

const numberFilter = ref<string | undefined>(undefined)
const policyGroupCreationDialogVisible = ref<boolean>(false)

const policyGroupUpdateDialogVisible = ref<boolean>(false)
const policyGroupBeingUpdated = ref<IPolicyGroup>()

// Query and mutation
const { policyGroupsQuery } = usePolicies()

// Document viewing
const viewDocument = (group: IPolicyGroup) => {
  window.open(group.signed_doc_url, '_blank')
}

const onEdit = (group: IPolicyGroup) => {
  policyGroupBeingUpdated.value = group
  policyGroupUpdateDialogVisible.value = true
}

const onCustomerChange = (customer: any) => {
  filters.customer_id = customer?.id
}

// Server data
const { data: tableData, isRefetching: sourceDataPending } = policyGroupsQuery(filters)

const handleNumberInputChange = useDebounceFn((query: string) => {
  filters.number = query.trim() || undefined
}, 500)
</script>

<template>
  <div class="flex">
    <div class="flex gap-3">
      <el-input
        v-model="numberFilter"
        style="width: 232px"
        placeholder="Number"
        clearable
        :loading="sourceDataPending"
        :disabled="sourceDataPending"
        :suffix-icon="Search"
        @input="handleNumberInputChange"
      />
      <el-select
        v-model="filters.status"
        style="width: 232px"
        placeholder="Status"
        clearable
        :disabled="sourceDataPending"
        :loading="sourceDataPending"
      >
        <el-option v-for="opt in Object.values(PolicyGroupStatus)" :key="opt" :label="opt" :value="opt" />
      </el-select>

      <RcCustomerSelect
        v-if="!isCustomer && !props.customerId"
        class="w-[232px]"
        :disabled="sourceDataPending"
        @change="onCustomerChange"
      />
    </div>
    <div class="flex flex-1 justify-end gap-xs">
      <slot name="controls"></slot>
    </div>
  </div>
  <!-- Table -->
  <el-table v-loading="sourceDataPending" :data="tableData?.data" row-key="number">
    <el-table-column v-if="!props.customerId && !isCustomer" prop="customer.name" label="Customer" min-width="160" />
    <el-table-column prop="number" label="Policy Group #" min-width="120">
      <template #default="scope">
        <copy-button :value="scope.row.number"> {{ scope.row.number }} </copy-button>
      </template>
    </el-table-column>
    <el-table-column label="Status" min-width="80">
      <template #default="{ row }">
        <StatusBadge :status="row.status" />
      </template>
    </el-table-column>
    <el-table-column prop="effective_date" label="Effective Date" :formatter="dateFormatter" />
    <el-table-column prop="initial_term_months" label="Initial Term (mo)" />
    <el-table-column prop="subsequent_term_months" label="Subsequent Term (mo)" />
    <el-table-column prop="total_policies" label="Total Policies">
      <template #default="{ row }">
        <div class="flex gap-2">
          <span>{{ row.total_policies }}</span>
          <el-tooltip effect="light" placement="top">
            <template #content>
              <p v-for="(name, amount) in row.policies_by_status" :key="name">{{ amount }}: {{ name }}</p>
            </template>
            <el-button link :icon="QuestionFilled" />
          </el-tooltip>
        </div>
      </template>
    </el-table-column>
    <el-table-column :width="80">
      <template #default="{ row }">
        <el-button link title="View Agreement" @click="viewDocument(row as IPolicyGroup)">
          <Icon icon="material-symbols:download" :height="16" />
        </el-button>
        <el-button v-if="isASICWOperationalUser" link title="Edit" @click="onEdit(row as IPolicyGroup)">
          <Icon icon="material-symbols:edit-outline" :height="16" />
        </el-button>
      </template>
    </el-table-column>
  </el-table>
  <el-pagination
    v-if="tableData && tableData.total_pages > 0"
    v-model:current-page="filters.page"
    v-model:page-size="filters.per_page"
    :page-sizes="DEFAULT_PAGE_SIZES"
    :page-count="tableData.total_pages"
    :total="tableData.total_records"
    :disabled="sourceDataPending"
    :layout="DEFAULT_PAGINATION_LAYOUT"
    background
    class="self-end"
  />
  <CreatePolicyDialog v-model="policyGroupCreationDialogVisible" />
  <UpdatePolicyGroupDialog
    v-if="policyGroupBeingUpdated"
    v-model="policyGroupUpdateDialogVisible"
    :group="policyGroupBeingUpdated"
  />
</template>
